import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface MentorVisibilityToggleProps {
  mentorId: string;
  isActive: boolean;
  onVisibilityChange?: (isActive: boolean) => void;
  showCard?: boolean;
}

export const MentorVisibilityToggle: React.FC<MentorVisibilityToggleProps> = ({
  mentorId,
  isActive,
  onVisibilityChange,
  showCard = true
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const { toast } = useToast();

  const handleToggleVisibility = async (checked: boolean) => {
    setIsUpdating(true);
    
    try {
      const { error } = await supabase
        .from('mentors')
        .update({ is_active: checked })
        .eq('id', mentorId);

      if (error) throw error;

      onVisibilityChange?.(checked);
      
      toast({
        title: checked ? "Now Available" : "Now Hidden",
        description: checked 
          ? "You are now visible to students and can receive bookings." 
          : "You are now hidden from students and won't receive new bookings.",
      });
    } catch (error) {
      console.error('Error updating visibility:', error);
      toast({
        title: "Error",
        description: "Failed to update visibility. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const content = (
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-3">
        <div className="flex items-center space-x-2">
          {isActive ? (
            <Eye className="h-5 w-5 text-mentor-primary" />
          ) : (
            <EyeOff className="h-5 w-5 text-muted-foreground" />
          )}
          <div>
            <Label htmlFor="visibility-toggle" className="text-sm font-medium">
              Available for Booking
            </Label>
            <p className="text-xs text-muted-foreground">
              {isActive 
                ? "Students can see you and book sessions" 
                : "You're hidden from students"
              }
            </p>
          </div>
        </div>
      </div>
      <Switch
        id="visibility-toggle"
        checked={isActive}
        onCheckedChange={handleToggleVisibility}
        disabled={isUpdating}
      />
    </div>
  );

  if (!showCard) {
    return content;
  }

  return (
    <Card className="obsidian-card">
      <CardHeader>
        <CardTitle className="text-energy flex items-center gap-2">
          <Eye className="h-5 w-5" />
          Availability Status
        </CardTitle>
      </CardHeader>
      <CardContent>
        {content}
      </CardContent>
    </Card>
  );
};